class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initUpsell();
    this.initEmptyCartProductsSlider();
    this.initMutationObserver();
  }

  initMutationObserver() {
    if (this._observer) return;
    this._observer = new MutationObserver((mutations) => {
      const shouldInit = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            node.id === 'cart-drawer-upsell-slider' || 
            node.classList.contains('cart-drawer-upsell') ||
            node.querySelector('#cart-drawer-upsell-slider') ||
            node.id === 'Slider-cart-drawer-empty' ||
            node.querySelector('#Slider-cart-drawer-empty')
          )
        )
      );
      if (shouldInit) {
        if (this._initTimeout) clearTimeout(this._initTimeout);
        this._initTimeout = setTimeout(() => {
          this.initUpsell();
          this.initEmptyCartProductsSlider();
          requestAnimationFrame(() => this.refreshEmptyCartGalleries());
          setTimeout(() => this.refreshEmptyCartGalleries(), 50);
        }, 50);
      }
    });

    this._observer.observe(this, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.initEmptyCartProductsSlider();
      requestAnimationFrame(() => this.refreshEmptyCartGalleries());
      setTimeout(() => this.refreshEmptyCartGalleries(), 50);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    // document.body.classList.remove('overflow-hidden');
    const menuDrawerOpen = document.querySelector('menu-drawer details[open], header-drawer details[open]');
    if (!menuDrawerOpen) {
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('overflow-hidden');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    
    if (parsedState.item_count === 0) {
      this.classList.add('is-empty');
    } else {
      this.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (!this.classList.contains('active')) {
        this.open();
      }
      this.initUpsell();
      this.initEmptyCartProductsSlider();
      requestAnimationFrame(() => this.refreshEmptyCartGalleries());
      setTimeout(() => this.refreshEmptyCartGalleries(), 50);
    });
  }

  refreshEmptyCartGalleries(attempt = 0) {
    const galleries = this.querySelectorAll('#Slider-cart-drawer-empty product-card-slider');
    if (!galleries.length) return;

    let needsRetry = false;
    galleries.forEach((gallery) => {
      if (gallery.slider && typeof gallery.slider.update === 'function') {
        gallery.slider.update();
      } else if (typeof gallery.initSlider === 'function') {
        gallery.initSlider();
        needsRetry = true;
      } else {
        needsRetry = true;
      }
    });

    if (needsRetry && attempt < 12) {
      setTimeout(() => this.refreshEmptyCartGalleries(attempt + 1), 100);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : doc.body.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initUpsell() {
    const sliderContainer = this.querySelector('#cart-drawer-upsell-slider');
    const upsellWrapper = this.querySelector('.cart-drawer-upsell');
    if (!sliderContainer || sliderContainer.classList.contains('initialized')) return;
    
    // Check if we need to load KeenSlider
    if (typeof KeenSlider === 'undefined') {
      const scriptUrl = this.dataset.keenSliderUrl;
      if (scriptUrl && !document.querySelector(`script[src="${scriptUrl}"]`)) {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.onload = () => this.initUpsell();
        document.head.appendChild(script);
        return;
      } else if (scriptUrl) {
         setTimeout(() => this.initUpsell(), 100);
         return;
      }
    }

    const slides = sliderContainer.querySelectorAll('.cart-drawer-upsell__slide');
    const dotsContainer = this.querySelector('.cart-drawer-upsell__dots');

    if (slides.length <= 1) {
        if (dotsContainer) dotsContainer.style.display = 'none';
        sliderContainer.style.marginLeft = '0';
        sliderContainer.classList.add('initialized');
        if (upsellWrapper) upsellWrapper.classList.add('initialized');
    } else {
        if (dotsContainer) dotsContainer.style.display = 'flex';
    }

    sliderContainer.querySelectorAll('.cart-drawer-upsell__form').forEach(form => {
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const submitBtn = form.querySelector('button');
            if (submitBtn.classList.contains('loading')) return;

            submitBtn.classList.add('loading');
            const spinner = submitBtn.querySelector('.loading__spinner');
            const iconSvg = submitBtn.querySelector('.cart-drawer-upsell__icon-svg');
            
            if (spinner) spinner.classList.remove('hidden');
            if (iconSvg) iconSvg.classList.add('hidden');

            const formData = new FormData(form);
            try {
                const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    body: formData
                });
                const data = await response.json();
                
                if (data.status) {
                    console.error('Error adding to cart:', data.description);
                    return;
                }

                if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'upsell', cartData: data });
                } else {
                    fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble&t=${Date.now()}`)
                    .then(res => res.json())
                    .then(res => {
                        this.renderContents({ sections: res });
                    });
                }
            } catch (error) {
                console.error('Error adding to cart:', error);
            } finally {
                submitBtn.classList.remove('loading');
                const spinner = submitBtn.querySelector('.loading__spinner');
                const iconSvg = submitBtn.querySelector('.cart-drawer-upsell__icon-svg');
                
                if (spinner) spinner.classList.add('hidden');
                if (iconSvg) iconSvg.classList.remove('hidden');
            }
        });
    });

    sliderContainer.querySelectorAll('.cart-drawer-upsell__variant-selector').forEach(selector => {
        const selectedDiv = selector.querySelector('.cart-drawer-upsell__selected-variant');
        const list = selector.querySelector('.cart-drawer-upsell__variant-list');
        const itemContainer = selector.closest('.cart-drawer-upsell__item');
        const form = itemContainer.querySelector('.cart-drawer-upsell__form');
        const hiddenInput = form.querySelector('input[name="id"]');
        const submitBtn = form.querySelector('button');

        selectedDiv.addEventListener('click', (e) => {
            e.stopPropagation();
            const isActive = selector.classList.toggle('active');
            list.classList.toggle('hidden', !isActive);
            
            sliderContainer.querySelectorAll('.cart-drawer-upsell__variant-selector').forEach(other => {
                if (other !== selector) {
                    other.classList.remove('active');
                    other.querySelector('.cart-drawer-upsell__variant-list').classList.add('hidden');
                }
            });
        });

        list.querySelectorAll('.cart-drawer-upsell__variant-item').forEach(item => {
            item.addEventListener('click', (e) => {
                e.stopPropagation();
                const variantId = item.dataset.variantId;
                const price = item.dataset.price;
                const title = item.dataset.variantTitle;
                const swatch = item.querySelector('.swatch');

                hiddenInput.value = variantId;
                // submitBtn.innerHTML = `ADD ${price}`; // REMOVED: Do not overwrite icon with text

                const selectedInfo = selectedDiv.querySelector('.cart-drawer-upsell__selected-variant-info');
                // selectedInfo.innerHTML = '';
                // if (swatch) {
                //     selectedInfo.appendChild(swatch.cloneNode(true));
                // }
                const nameSpan = selectedInfo.querySelector('.cart-drawer-upsell__selected-variant-name');
                if (nameSpan) {
                    nameSpan.textContent = title;
                }
                // const nameSpan = document.createElement('span');
                // nameSpan.className = 'cart-drawer-upsell__selected-variant-name';
                // nameSpan.textContent = title;
                // selectedInfo.appendChild(nameSpan);

                selector.classList.remove('active');
                list.classList.add('hidden');
            });
        });
    });
    sliderContainer.querySelectorAll('.cart-drawer-upsell__show-more-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const container = btn.closest('.cart-drawer-upsell__swatches');
            if (container) {
                container.querySelectorAll('.cart-drawer-upsell__swatch-btn.is-hidden').forEach(el => {
                    el.classList.remove('is-hidden');
                });
                btn.remove();
            }
        });
    });
    sliderContainer.querySelectorAll('.cart-drawer-upsell__swatches').forEach(swatchContainer => {
        const itemContainer = swatchContainer.closest('.cart-drawer-upsell__item');
        const form = itemContainer.querySelector('.cart-drawer-upsell__form');
        const hiddenInput = form.querySelector('input[name="id"]');
        const submitBtn = form.querySelector('button');
        const validationMsg = submitBtn.querySelector('.cart-drawer-upsell__validation-msg');

        // Validation on submit
        submitBtn.addEventListener('click', (e) => {
            if (!hiddenInput.value) {
                e.preventDefault();
                e.stopPropagation();
                if (validationMsg) {
                    validationMsg.classList.add('active');
                    setTimeout(() => {
                        validationMsg.classList.remove('active');
                    }, 3000);
                }
            }
        });

        swatchContainer.querySelectorAll('.cart-drawer-upsell__swatch-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                const variantId = btn.dataset.variantId;
                const price = btn.dataset.price;
                
                // Update hidden input
                hiddenInput.value = variantId;
                
                // Hide validation message if visible
                if (validationMsg) validationMsg.classList.remove('active');
                
                // Update button text
                // submitBtn.innerHTML = `ADD`; // REMOVED: Do not overwrite icon with text
                
                // Ensure loader is hidden and icon is visible
                const spinner = submitBtn.querySelector('.loading__spinner');
                const iconSvg = submitBtn.querySelector('.cart-drawer-upsell__icon-svg');
                if (spinner) spinner.classList.add('hidden');
                if (iconSvg) iconSvg.style.visibility = 'visible';

                // Update active state
                swatchContainer.querySelectorAll('.cart-drawer-upsell__swatch-btn').forEach(other => {
                    other.classList.remove('active');
                    other.classList.remove('tooltip-visible');
                });
                btn.classList.add('active');
                btn.classList.add('tooltip-visible');

                if (btn.tooltipTimeout) {
                    clearTimeout(btn.tooltipTimeout);
                }
                
                btn.tooltipTimeout = setTimeout(() => {
                    btn.classList.remove('tooltip-visible');
                    btn.tooltipTimeout = null;
                }, 2000);
            });
        });
        const showMoreBtn = swatchContainer.querySelector('.cart-drawer-upsell__show-more-btn');
        if (showMoreBtn) {
            showMoreBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                swatchContainer.querySelectorAll('.cart-drawer-upsell__swatch-btn.is-hidden').forEach(btn => {
                    btn.classList.remove('is-hidden');
                });
                
                showMoreBtn.style.display = 'none';
            });
        }
    });
    
    if (slides.length > 1 && typeof KeenSlider !== 'undefined') {
        if (sliderContainer.sliderInstance) {
            sliderContainer.sliderInstance.destroy();
        }
        
        requestAnimationFrame(() => {
            if (!sliderContainer.isConnected) return;
            
            sliderContainer.sliderInstance = new KeenSlider(sliderContainer, {
                slides: {
                    perView: 1,
                    spacing: 10,
                },
                breakpoints: {
                    '(min-width: 768px)': {
                        slides: { perView: 1, spacing: 10 }
                    }
                },
                detailsChanged: (s) => {
                   // No scrollbar update needed
                },
                slideChanged: (s) => {
                    if (!dotsContainer) return;
                    const dots = dotsContainer.querySelectorAll('.cart-drawer-upsell__dot');
                    dots.forEach((dot, idx) => {
                         if (idx === s.track.details.rel) {
                             dot.classList.add('active');
                         } else {
                             dot.classList.remove('active');
                         }
                    });
                },
                created: (s) => {
                    sliderContainer.classList.add('initialized');
                    if (upsellWrapper) upsellWrapper.classList.add('initialized');
                    
                    // Create dots
                    if (dotsContainer) {
                        dotsContainer.innerHTML = '';
                        s.track.details.slides.forEach((_slide, idx) => {
                            const dot = document.createElement('button');
                            dot.classList.add('cart-drawer-upsell__dot');
                            if (idx === s.track.details.rel) {
                                dot.classList.add('active');
                            }
                            dot.addEventListener('click', () => {
                                s.moveToIdx(idx);
                            });
                            dotsContainer.appendChild(dot);
                        });
                    }
                }
            });
        });
    }

    if (!window.cartDrawerUpsellClickAttached) {
        document.addEventListener('click', () => {
            const activeSelectors = document.querySelectorAll('.cart-drawer-upsell__variant-selector.active');
            activeSelectors.forEach(selector => {
                selector.classList.remove('active');
                selector.querySelector('.cart-drawer-upsell__variant-list').classList.add('hidden');
            });
        });
        window.cartDrawerUpsellClickAttached = true;
    }
  }

  initEmptyCartProductsSlider() {
    const slider = this.querySelector('#Slider-cart-drawer-empty.keen-slider');
    if (!slider) return;

    const slides = slider.querySelectorAll('.cart-drawer-empty__slide');
    const dotsContainer = slider.parentElement?.querySelector('.cart-drawer-empty__dots');

    if (slides.length <= 1) {
      if (dotsContainer) dotsContainer.style.display = 'none';
      slider.classList.add('initialized');
      return;
    }

    if (typeof KeenSlider === 'undefined') {
      const scriptUrl = this.dataset.keenSliderUrl;
      if (scriptUrl && !document.querySelector(`script[src="${scriptUrl}"]`)) {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.onload = () => this.initEmptyCartProductsSlider();
        document.head.appendChild(script);
        return;
      } else if (scriptUrl) {
        setTimeout(() => this.initEmptyCartProductsSlider(), 100);
        return;
      }
    }

    if (slider.sliderInstance) {
      slider.sliderInstance.destroy();
    }

    requestAnimationFrame(() => {
      if (!slider.isConnected) return;

      if (slider.dataset.emptyCartInteractiveGuard !== '1') {
        slider.dataset.emptyCartInteractiveGuard = '1';
        slider.querySelectorAll('product-card-slider').forEach((gallery) => {
          gallery.setAttribute('data-keen-slider-clickable', 'true');
        });
      }

      slider.sliderInstance = new KeenSlider(slider, {
        selector: '.cart-drawer-empty__slide',
        loop: false,
        rubberband: false,
        slides: {
          perView: 1.3,
          spacing: 5,
        },
        breakpoints: {
          '(min-width: 768px)': {
            slides: { perView: 1.3, spacing: 5 },
          },
        },
        slideChanged: (s) => {
          if (!dotsContainer) return;
          const dots = dotsContainer.querySelectorAll('.cart-drawer-upsell__dot');
          const activeIndex = Math.min(s.track.details.rel, dots.length - 1);
          dots.forEach((dot, idx) => {
            if (idx === activeIndex) {
              dot.classList.add('active');
            } else {
              dot.classList.remove('active');
            }
          });
        },
        created: (s) => {
          slider.classList.add('initialized');
          requestAnimationFrame(() => this.refreshEmptyCartGalleries());
          setTimeout(() => this.refreshEmptyCartGalleries(), 50);

          const buildDots = () => {
            if (!dotsContainer) return;
            const dotCount = slides.length;

            if (dotCount <= 1) {
              dotsContainer.style.display = 'none';
              dotsContainer.innerHTML = '';
              return;
            }

            dotsContainer.style.display = 'flex';
            dotsContainer.innerHTML = '';
            const activeIndex = Math.min(s.track.details.rel, dotCount - 1);
            for (let idx = 0; idx < dotCount; idx += 1) {
              const dot = document.createElement('button');
              dot.classList.add('cart-drawer-upsell__dot');
              if (idx === activeIndex) {
                dot.classList.add('active');
              }
              dot.addEventListener('click', () => {
                s.moveToIdx(idx);
              });
              dotsContainer.appendChild(dot);
            }
          };

          buildDots();
          s.on('updated', buildDots);
          s.on('optionsChanged', buildDots);
        },
      });
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
